"use client";

import { isPaidPlan, type UserPlan } from "@/lib/auth/plans";

import { useCallback, useEffect, useState } from "react";
import { useUser } from "./useUser";
import type { SubscriptionStatus } from "@/lib/auth/subscription";

/**
 * Логіка "коли і що нагадувати про підписку" — анти-зайобування за дизайном:
 * активна підписка з робочою карткою НЕ отримує жодних нагадувань. Нотис
 * існує рівно у 3 станах, всі три юзеру вигідні:
 *  - past_due: платіж не пройшов → "онови картку" (найпомітніший, повертається
 *    раз на добу, поки не вирішено);
 *  - ending: скасована підписка догорає, лишилось ≤3 дні → "Resume?"
 *    (один показ на білінговий період);
 *  - expired: Pro щойно закінчився (≤14 днів) → "Renew" (один показ).
 */
export type SubscriptionNotice =
  | { kind: "past_due" }
  | { kind: "ending"; daysLeft: number; endsAt: string }
  | { kind: "expired"; endedAt: string };

const STORAGE_KEY = "subscription-notice-dismissals";
/** past_due повертається через добу після dismiss — він урджентний. */
const PAST_DUE_SNOOZE_MS = 24 * 60 * 60 * 1000;
/** "Pro закінчився" показуємо лише перші 14 днів після дати. */
const EXPIRED_NOTICE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
/** Банер "догорає" з'являється за ≤3 дні до кінця. */
const ENDING_SOON_DAYS = 3;
/** Прибирання старих dismiss-записів, щоб мапа не росла вічно. */
const PRUNE_AFTER_MS = 90 * 24 * 60 * 60 * 1000;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type NoticeInput = {
  hasUser: boolean;
  plan: string;
  planExpiresAt: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  isLapsedPro: boolean;
};

export function computeSubscriptionNotice(
  input: NoticeInput,
  now: Date = new Date(),
): SubscriptionNotice | null {
  const { hasUser, plan, planExpiresAt, subscriptionStatus, isLapsedPro } = input;
  if (!hasUser) return null;

  // 1) Платіж не пройшов — найвищий пріоритет, показуємо незалежно від
  //    ефективного плану (навіть якщо grace вже вичерпано).
  if (subscriptionStatus === "past_due") {
    return { kind: "past_due" };
  }

  const expiresMs = planExpiresAt ? new Date(planExpiresAt).getTime() : null;

  // 2) Скасована підписка догорає — лише в останні ENDING_SOON_DAYS днів.
  if (
    subscriptionStatus === "scheduled_cancel" &&
    isPaidPlan(plan as UserPlan) &&
    expiresMs !== null &&
    expiresMs > now.getTime()
  ) {
    const daysLeft = Math.ceil((expiresMs - now.getTime()) / MS_PER_DAY);
    if (daysLeft <= ENDING_SOON_DAYS) {
      return { kind: "ending", daysLeft, endsAt: planExpiresAt! };
    }
    return null;
  }

  // 3) Pro щойно закінчився (webhook-даунгрейд зберігає plan_expires_at, а
  //    lapsed-pro рядок так само тримає сиру дату).
  if (
    !isPaidPlan(plan as UserPlan) &&
    expiresMs !== null &&
    expiresMs <= now.getTime() &&
    now.getTime() - expiresMs <= EXPIRED_NOTICE_WINDOW_MS &&
    (subscriptionStatus === "canceled" ||
      subscriptionStatus === "scheduled_cancel" ||
      isLapsedPro)
  ) {
    return { kind: "expired", endedAt: planExpiresAt! };
  }

  return null;
}

function noticeKey(notice: SubscriptionNotice, planExpiresAt: string | null): string {
  // Ключ включає дату періоду → новий білінговий період автоматично "скидає"
  // dismiss без окремої логіки.
  return `${notice.kind}:${planExpiresAt ?? "na"}`;
}

type DismissalMap = Record<string, number>;

function readDismissals(): DismissalMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as DismissalMap) : {};
  } catch {
    return {};
  }
}

function writeDismissals(map: DismissalMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage може бути недоступний (private mode) — нотис просто
    // показуватиметься знову; не критично.
  }
}

/**
 * Нотис для банера — з урахуванням dismiss-стану в localStorage.
 * `notice` = null, поки нема чого показувати АБО поки dismissals ще не
 * прочитані (уникаємо флешу показав-сховав).
 */
export function useSubscriptionNotice(): {
  notice: SubscriptionNotice | null;
  dismiss: () => void;
} {
  const { user, plan, planExpiresAt, subscriptionStatus, isLapsedPro, loading } =
    useUser();

  const [dismissals, setDismissals] = useState<DismissalMap | null>(null);

  useEffect(() => {
    const map = readDismissals();
    // Prune старих записів (разово при монтуванні).
    const cutoff = Date.now() - PRUNE_AFTER_MS;
    let changed = false;
    for (const key of Object.keys(map)) {
      if (map[key] < cutoff) {
        delete map[key];
        changed = true;
      }
    }
    if (changed) writeDismissals(map);
    setDismissals(map);

    // Cross-tab: dismiss в іншій вкладці ховає банер і тут.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setDismissals(readDismissals());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const raw =
    loading || dismissals === null
      ? null
      : computeSubscriptionNotice({
          hasUser: !!user,
          plan,
          planExpiresAt,
          subscriptionStatus,
          isLapsedPro,
        });

  let notice: SubscriptionNotice | null = raw;
  if (raw && dismissals) {
    const dismissedAt = dismissals[noticeKey(raw, planExpiresAt)];
    if (dismissedAt) {
      const snoozed =
        raw.kind === "past_due"
          ? Date.now() - dismissedAt < PAST_DUE_SNOOZE_MS
          : true; // ending/expired: dismiss назавжди (для цього періоду)
      if (snoozed) notice = null;
    }
  }

  const dismiss = useCallback(() => {
    if (!raw) return;
    const map = readDismissals();
    map[noticeKey(raw, planExpiresAt)] = Date.now();
    writeDismissals(map);
    setDismissals(map);
  }, [raw, planExpiresAt]);

  return { notice, dismiss };
}

/**
 * Пасивний індикатор для аватарки/дропдауна — ІГНОРУЄ dismiss (крапка на
 * аватарці лишається, навіть коли банер закрили). Для scheduled_cancel не
 * чекає останніх 3 днів — "Pro · until Aug 15" видно одразу.
 */
export function useSubscriptionAttention(): {
  /** Є що показати (жовтогаряча крапка на аватарці). */
  attention: boolean;
  /** Короткий підпис для рядка Plan у дропдауні (null = нічого особливого). */
  shortLabel: string | null;
} {
  const { user, plan, planExpiresAt, subscriptionStatus, isLapsedPro, loading } =
    useUser();

  if (loading || !user) return { attention: false, shortLabel: null };

  if (subscriptionStatus === "past_due") {
    return { attention: true, shortLabel: "Payment issue" };
  }

  if (
    subscriptionStatus === "scheduled_cancel" &&
    isPaidPlan(plan as UserPlan) &&
    planExpiresAt
  ) {
    return {
      attention: true,
      shortLabel: `Until ${formatShortDate(planExpiresAt)}`,
    };
  }

  const notice = computeSubscriptionNotice({
    hasUser: true,
    plan,
    planExpiresAt,
    subscriptionStatus,
    isLapsedPro,
  });
  if (notice?.kind === "expired") {
    return { attention: true, shortLabel: "Expired" };
  }

  return { attention: false, shortLabel: null };
}

export function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
